/**
 * savedReportsService.js
 *
 * Phase 7A — Persistence/CRUD layer for saved_reports. Pure data access —
 * no UI, no Report Builder logic. This is the ONLY module that talks to
 * the saved_reports table directly.
 *
 * Three-layer contract (approved Gate, Phase 7A + Temporal Intent
 * correction in Phase 7B):
 *   analytical_definition  — { query: AnalyticalQuery, temporalInput? }
 *                             query is savedDefinition's own query as-is
 *                             (dataset, measures, dimension, comparison,
 *                             time, filters) — never
 *                             buildEffectiveQuery()'s output.
 *                             temporalInput is ONLY present for
 *                             dataset === 'time_series', value always
 *                             { mode: 'all_available' } in v1.
 *   visualization           — { type, mode?, yAxis? } — the
 *                             VisualDefinition's presentation-agnostic
 *                             part (Phase 5B minimal contract).
 *   presentation             — { title: string, notes?: string,
 *                             position?: {x,y,w,h} } ONLY. notes (Stage 8)
 *                             is free-text commentary for the printed
 *                             report layout — intentionally allowed, not
 *                             Interaction State. Per the approved Gate,
 *                             drillDown/crossFilters/topN/sorting are
 *                             explicitly FORBIDDEN here — this module
 *                             validates that and refuses to save/update
 *                             a report carrying any of them, rather than
 *                             silently accepting and persisting
 *                             Interaction State.
 */

import { supabase } from './supabase'
import { validateQuery } from './analyticalQuery'
import { VISUAL_TYPES } from './visualContract'
import { COMPOSITE_OUTPUTS } from './compositeOutputs'

const FORBIDDEN_PRESENTATION_KEYS = ['drillDown', 'crossFilters', 'topN', 'sorting']

/**
 * Validates the three-layer shape before any write. Throws with a
 * specific reason rather than letting a malformed row reach the
 * database — the same "fail loud, not silent" posture used throughout
 * this project's Adapters.
 */
/**
 * analytical_definition shape (Phase 7B, Temporal Intent Contract,
 * approved): a wrapper around the existing AnalyticalQuery, NOT the
 * query itself directly — this is a contract change from Phase 7A's
 * original (simpler) assumption that analytical_definition WAS the
 * AnalyticalQuery. That assumption is corrected here, not worked around:
 *
 *   analytical_definition = {
 *     query: AnalyticalQuery,        // dataset/measures/dimension/
 *                                     // comparison/time/filters, exactly
 *                                     // as validateQuery() already expects
 *     temporalInput?: {               // ONLY for dataset === 'time_series'
 *       mode: 'all_available'         // the only value in v1 — see
 *     }                                // Phase 7B's Temporal Intent Gate
 *   }
 *
 * monthly_actual_cost carries NO temporalInput at all — its Owner
 * discovers periods automatically, so there is nothing to persist as
 * intent for it. cost_performance/historical_comparison never have
 * temporalInput either (they have no temporal dimension in the first
 * place).
 */
function validateSavedReportShape({ analytical_definition, visualization, presentation }) {
  if (!analytical_definition || typeof analytical_definition !== 'object') {
    throw new Error('analytical_definition must be an object')
  }

  // Stage 8: analytical_definition now carries an explicit outputType —
  // 'dataset' (query-based, the original Phase 7A/7B shape) or
  // 'composite' (S-Curve, no AnalyticalQuery at all). A definition with
  // no outputType is treated as 'dataset' for backward compatibility
  // with reports saved before this Stage.
  const outputType = analytical_definition.outputType || 'dataset'

  if (outputType === 'composite') {
    if (!COMPOSITE_OUTPUTS[analytical_definition.compositeKey]) {
      throw new Error(
        `analytical_definition.compositeKey "${analytical_definition.compositeKey}" is not a known composite output.`
      )
    }
    // Composite outputs have no query/measures/dimension to validate —
    // their shape is entirely defined by COMPOSITE_OUTPUTS itself.
  } else if (outputType === 'dataset') {
    const { valid, errors } = validateQuery(analytical_definition.query)
    if (!valid) {
      throw new Error(`Invalid analytical_definition.query: ${errors.join('; ')}`)
    }

    const dataset = analytical_definition.query.dataset
    if (analytical_definition.temporalInput != null) {
      if (dataset !== 'time_series') {
        throw new Error(
          `temporalInput is only valid for dataset "time_series" — got it on "${dataset}". ` +
          `monthly_actual_cost discovers periods automatically and must not carry temporalInput.`
        )
      }
      if (analytical_definition.temporalInput.mode !== 'all_available') {
        throw new Error(
          `temporalInput.mode must be "all_available" — "${analytical_definition.temporalInput.mode}" ` +
          `is not supported in v1 (e.g. "last_n" is explicitly out of scope).`
        )
      }
    }
  } else {
    throw new Error(`analytical_definition.outputType must be 'dataset' or 'composite', got "${outputType}"`)
  }

  if (!visualization || typeof visualization !== 'object') {
    throw new Error('visualization must be an object')
  }
  if (!VISUAL_TYPES.includes(visualization.type)) {
    throw new Error(`visualization.type must be one of ${VISUAL_TYPES.join(', ')}, got "${visualization.type}"`)
  }

  if (!presentation || typeof presentation !== 'object') {
    throw new Error('presentation must be an object')
  }
  if (!presentation.title || typeof presentation.title !== 'string') {
    throw new Error('presentation.title is required and must be a string')
  }
  if (presentation.position != null) {
    const { x, y, w, h } = presentation.position
    if ([x, y, w, h].some(v => typeof v !== 'number')) {
      throw new Error('presentation.position, if provided, must have numeric x/y/w/h')
    }
  }
  // Reject Interaction State smuggled into presentation — this is the
  // enforcement of the approved Gate's explicit prohibition, not just a
  // documentation comment.
  const leakedKeys = FORBIDDEN_PRESENTATION_KEYS.filter(k => k in presentation)
  if (leakedKeys.length > 0) {
    throw new Error(
      `presentation must not contain Interaction State keys: ${leakedKeys.join(', ')} — ` +
      `drillDown/crossFilters/topN/sorting are ephemeral and must never be persisted.`
    )
  }
  // Also guard analytical_definition itself — a caller must never pass
  // buildEffectiveQuery()'s output (which may carry drill-down-derived
  // filters) as the saved definition. There is no reliable way to detect
  // "this filter came from a drill-down" purely from the query shape, so
  // this is a documented caller responsibility (see saveReport()'s JSDoc)
  // rather than a runtime check — the shape of AnalyticalQuery.filters is
  // legitimately used for both saved base filters and interaction-derived
  // ones, and collapsing that distinction here would be guessing.
}

/**
 * @param {string} projectId
 * @param {{analytical_definition, visualization, presentation}} report
 *   IMPORTANT: analytical_definition must be the Saved Query itself
 *   (savedDefinition.query) — NEVER the output of buildEffectiveQuery().
 *   Passing an effective query here would persist Interaction State
 *   (drill-down filters) as if it were the base definition, violating
 *   the approved Gate.
 * @param {string} [createdBy] - free-text attribution, not a user_id FK
 */
export async function saveReport(projectId, report, createdBy = null) {
  if (!projectId) throw new Error('saveReport: projectId is required')
  validateSavedReportShape(report)

  const { data, error } = await supabase
    .from('saved_reports')
    .insert({
      project_id: projectId,
      analytical_definition: report.analytical_definition,
      visualization: report.visualization,
      presentation: report.presentation,
      created_by: createdBy,
    })
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Lists saved reports for a project — lightweight (no full JSONB
 * payload) for a report picker/list UI. Use loadReport() to get the
 * full definition for one report.
 */
export async function listReports(projectId) {
  if (!projectId) throw new Error('listReports: projectId is required')

  const { data, error } = await supabase
    .from('saved_reports')
    .select('id, presentation, created_by, created_at, updated_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Loads one report's full three-layer definition — ready to pass
 * directly into executeQuery()/executeTimeSeriesQuery()/
 * executeMonthlyActualCostQuery() (via its analytical_definition) and
 * the appropriate Visual component (via its visualization).
 */
export async function loadReport(reportId) {
  if (!reportId) throw new Error('loadReport: reportId is required')

  const { data, error } = await supabase
    .from('saved_reports')
    .select('*')
    .eq('id', reportId)
    .single()

  if (error) throw error
  return data
}

/**
 * Updates an existing report's definition. Same validation as save —
 * an update carrying Interaction State in presentation is rejected the
 * same way a save would be.
 */
export async function updateReport(reportId, report) {
  if (!reportId) throw new Error('updateReport: reportId is required')
  validateSavedReportShape(report)

  const { data, error } = await supabase
    .from('saved_reports')
    .update({
      analytical_definition: report.analytical_definition,
      visualization: report.visualization,
      presentation: report.presentation,
      updated_at: new Date().toISOString(),
    })
    .eq('id', reportId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteReport(reportId) {
  if (!reportId) throw new Error('deleteReport: reportId is required')

  const { error } = await supabase
    .from('saved_reports')
    .delete()
    .eq('id', reportId)

  if (error) throw error
  return true
}
